import { useContext, useEffect, useRef, useState } from "react";
import { ChatWindowContext } from "./chat-window-context";
import { Copy, Pencil, Reply, Trash2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { cn } from "@/shared/lib/utils";
import {
  messageDate,
  messageTimestamp,
  messageStatus,
  messageReaders,
  privateMediaUrl,
  REACTIONS,
} from "../lib/messages";
import { AiChip } from "./avatar";
import type { Message, Snapshot } from "../types";
import { HOVER_MUTED } from "./styles";

const BUBBLE_CLASS =
  "max-w-full min-w-0 rounded-2xl px-3 py-2 text-base leading-normal wrap-anywhere";

const MEDIA_CLASS = "mt-1 max-h-72 w-full rounded-sm object-contain";

const ACTION_ITEM_CLASS = cn(
  "min-h-11 w-full cursor-pointer justify-start px-2 text-[13px] font-normal",
  HOVER_MUTED,
);

const PICKER_BUTTON_CLASS = cn(
  "min-h-11 min-w-11 cursor-pointer text-xl",
  HOVER_MUTED,
);

interface MessageItemProps {
  message: Message;
  own: boolean;
  mentioned?: boolean;
  grouped?: boolean;
  snapshot?: Snapshot;
  busy: boolean;
  onReply: (message: Message) => void;
  onEdit: (message: Message) => void;
  onDelete: (message: Message) => void;
  onReact: (message: Message, emoji: string) => void;
  onError: (text: string) => void;
}

function MentionText({
  text,
  mentions,
}: {
  text: string;
  mentions: Message["mentions"];
}) {
  const names = mentions
    .map((mention) => `@${mention.name}`)
    .sort((a, b) => b.length - a.length);
  if (!names.length) return text;
  const escaped = names.map((name) =>
    name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  );
  return text
    .split(new RegExp(`(${escaped.join("|")})(?![\\p{L}\\p{N}_])`, "gu"))
    .map((part, index) =>
      names.includes(part) ? (
        <mark
          className="rounded-[3px] bg-accent/15 px-0.5 text-gold"
          key={index}
        >
          {part}
        </mark>
      ) : (
        part
      ),
    );
}

function MessageText({ message }: { message: Message }) {
  return message.text.split(/(https?:\/\/[^\s<>]+)/g).map((part, index) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={index}
        className="underline"
        href={part}
        target="_blank"
        rel="noopener noreferrer"
      >
        {part}
      </a>
    ) : (
      <MentionText key={index} text={part} mentions={message.mentions ?? []} />
    ),
  );
}

export function MessageItem({
  message,
  own,
  mentioned = false,
  grouped = false,
  snapshot,
  busy,
  onReply,
  onEdit,
  onDelete,
  onReact,
  onError,
}: MessageItemProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copied, setCopied] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const container = useContext(ChatWindowContext);
  const press = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const touchOrigin = useRef<{ x: number; y: number } | null>(null);
  const cancelPress = () => clearTimeout(press.current);
  useEffect(() => () => clearTimeout(press.current), []);
  const openActions = () => {
    if (!message.deleted) setActionsOpen(true);
  };
  const readers = messageReaders(message, snapshot);
  const status = messageStatus(message, snapshot);
  const sender = snapshot?.participants.find(
    (participant) => participant.memberId === message.memberId,
  );
  const ai = sender?.role === "ASSISTANT";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.text);
      setCopied(true);
    } catch {
      onError(
        "Clipboard access is unavailable. Select the message text to copy it.",
      );
    }
  };
  return (
    <article
      className={cn(
        "group/message relative flex flex-col items-start px-3",
        own && "items-end",
        grouped ? "mt-0.5" : "mt-3",
      )}
      onContextMenu={(event) => {
        if (message.deleted) return;
        event.preventDefault();
        openActions();
      }}
      onPointerDown={(event) => {
        if (
          event.pointerType !== "touch" ||
          (event.target as HTMLElement).closest("button, a, summary, video")
        )
          return;
        touchOrigin.current = { x: event.clientX, y: event.clientY };
        cancelPress();
        press.current = setTimeout(openActions, 500);
      }}
      onPointerMove={(event) => {
        const origin = touchOrigin.current;
        if (
          origin &&
          Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > 10
        )
          cancelPress();
      }}
      onPointerUp={cancelPress}
      onPointerCancel={cancelPress}
      aria-label={`${own ? "You" : message.memberName}: ${message.deleted ? "Message removed" : message.text}`}
    >
      {snapshot?.kind === "G" && (
        <div
          hidden={own || grouped}
          className={cn(
            "mb-0.5 flex items-center gap-1.5 px-2 text-xs text-muted-foreground",
            ai && "text-gold",
          )}
        >
          {own ? "You" : message.memberName}
          {ai && <AiChip rule={sender?.aiRule} />}
        </div>
      )}
      {mentioned && !message.deleted && (
        <span className="mb-[3px] text-xs text-gold-soft">
          You were mentioned
        </span>
      )}
      <div className="relative w-fit max-w-[min(85%,calc(100%-48px))]">
        <div
          className={cn(
            "sm-chat-bubble",
            own
              ? "sm-chat-bubble-own"
              : ai
                ? "sm-chat-bubble-handler"
                : "sm-chat-bubble-peer",
            message.deleted && "sm-chat-bubble-deleted",
            BUBBLE_CLASS,
            own
              ? "rounded-br-[6px] bg-primary text-primary-foreground"
              : "rounded-bl-[6px] bg-muted text-foreground",
            ai &&
              "border border-gold/40 bg-gradient-to-br from-secondary to-card text-parchment shadow-[0_0_24px_rgb(214_169_78/8%)]",
            message.deleted &&
              "bg-transparent bg-none text-muted-foreground italic shadow-none ring-1 ring-border",
            mentioned && !message.deleted && "ring-1 ring-gold",
          )}
        >
          {message.deleted ? (
            "Message removed"
          ) : (
            <>
              {message.replyTo && message.replyTo !== "0" && (
                <blockquote className="mb-1.5 max-h-16 overflow-hidden rounded-sm border-l-2 border-border bg-foreground/10 py-1 pr-2 pl-2 text-xs whitespace-pre-wrap">
                  {message.replyQuote || "Original message unavailable"}
                </blockquote>
              )}
              {message.text && (
                <p className="m-0 whitespace-pre-wrap">
                  <MessageText message={message} />
                </p>
              )}
              {message.attachments.map((attachment) => {
                const url = privateMediaUrl(attachment.url);
                if (attachment.status !== "CLEAR" || !url)
                  return (
                    <p key={attachment.attachId}>Attachment unavailable</p>
                  );
                return attachment.type === "IMAGE" ? (
                  <a
                    className="block"
                    key={attachment.attachId}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className={MEDIA_CLASS}
                      src={url}
                      alt={attachment.name || "Shared image"}
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <video
                    className={MEDIA_CLASS}
                    playsInline
                    key={attachment.attachId}
                    src={url}
                    controls
                    preload="metadata"
                    aria-label={attachment.name || "Shared video"}
                  />
                );
              })}
            </>
          )}
          <div
            className={cn(
              "mt-0.5 flex flex-wrap items-center justify-end gap-1 text-xs",
              own ? "text-primary-foreground/70" : "text-muted-foreground",
            )}
          >
            <time dateTime={message.datetime} title={messageDate(message)}>
              {Number.isNaN(messageTimestamp(message).getTime())
                ? message.datetime
                : messageTimestamp(message).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
            </time>
            {message.edited && (
              <span className="inline-flex items-center gap-[3px]">Edited</span>
            )}
          </div>
        </div>
        {!message.deleted && (
          <>
            {message.reactions.length > 0 && (
              <div
                className="mt-1 flex flex-wrap gap-1"
                aria-label="Message reactions"
              >
                {message.reactions.map((reaction) => (
                  <Button
                    key={reaction.emoji}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-11 cursor-pointer rounded-full border-border bg-card/80 px-1.5 text-xs font-normal aria-pressed:border-gold/60 aria-pressed:bg-accent/10 motion-reduce:transition-none dark:border-border dark:bg-card/80 dark:aria-pressed:border-gold/60 dark:aria-pressed:bg-accent/10"
                    aria-pressed={reaction.mine}
                    aria-label={`${REACTIONS.find((item) => item.emoji === reaction.emoji)?.label ?? reaction.emoji}, ${reaction.count} reaction${reaction.count === 1 ? "" : "s"}`}
                    disabled={busy}
                    onClick={() => onReact(message, reaction.emoji)}
                  >
                    {reaction.emoji} {reaction.count}
                  </Button>
                ))}
              </div>
            )}
            <Popover open={actionsOpen} onOpenChange={setActionsOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Message actions"
                  className="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:right-0 focus:z-10 focus:rounded-full focus:bg-card focus:px-3 focus:text-foreground"
                >
                  <span>Actions</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="relative z-12 flex max-h-[min(440px,calc(var(--chat-viewport-height,100dvh)-120px))] w-[min(280px,calc(100vw-24px))] flex-col overflow-y-auto rounded-xl border border-border bg-popover p-2 text-sm text-popover-foreground shadow-[0_10px_25px_rgb(0_0_0/45%)]"
                container={container}
                side="top"
                align={own ? "end" : "start"}
                sideOffset={6}
                collisionPadding={8}
                collisionBoundary={container}
                onEscapeKeyDown={(event) => event.stopPropagation()}
              >
                <div
                  className="grid grid-cols-3 gap-1 p-1.5 text-xl"
                  aria-label="React to message"
                >
                  {REACTIONS.map(({ emoji, label }) => (
                    <Button
                      key={emoji}
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={cn(PICKER_BUTTON_CLASS, "w-full")}
                      disabled={busy}
                      aria-label={`React ${label}`}
                      aria-pressed={message.reactions.some(
                        (reaction) => reaction.emoji === emoji && reaction.mine,
                      )}
                      onClick={() => onReact(message, emoji)}
                    >
                      {emoji}
                    </Button>
                  ))}
                </div>
                <div className="flex flex-col gap-0.5 pt-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={ACTION_ITEM_CLASS}
                    onClick={() => onReply(message)}
                  >
                    <Reply className="size-3.5" />
                    Reply
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={ACTION_ITEM_CLASS}
                    onClick={() => void copy()}
                  >
                    <Copy className="size-3.5" />
                    {copied ? "Copied" : "Copy"}
                  </Button>
                  {own && (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={ACTION_ITEM_CLASS}
                        disabled={busy}
                        onClick={() => onEdit(message)}
                      >
                        <Pencil className="size-3.5" />
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={ACTION_ITEM_CLASS}
                        disabled={busy}
                        onClick={() => setConfirmDelete(true)}
                      >
                        <Trash2 className="size-3.5" />
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            {confirmDelete && (
              <div
                className="mt-1.5 max-w-[85%] rounded-sm border border-border bg-card p-2.5"
                role="group"
                aria-label="Confirm deletion"
              >
                <p className="text-xs">
                  Remove this message from the conversation? This cannot undo a
                  reply already generated.
                </p>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="mt-1.5 min-h-11 cursor-pointer px-2 text-xs motion-reduce:transition-none"
                  disabled={busy}
                  onClick={() => {
                    onDelete(message);
                    setConfirmDelete(false);
                  }}
                >
                  Remove message
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "mt-1.5 min-h-11 cursor-pointer px-2 text-xs",
                    HOVER_MUTED,
                  )}
                  onClick={() => setConfirmDelete(false)}
                >
                  Cancel
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      {own &&
        !message.deleted &&
        (snapshot?.kind === "G" || status !== "Saved to server") && (
          <div className="mt-0.5 px-2 text-xs text-muted-foreground">
            {" "}
            {own && snapshot?.kind === "G" ? (
              <details className="relative text-right">
                <summary className="inline-flex min-h-11 min-w-11 cursor-pointer list-none items-center [&::-webkit-details-marker]:hidden">
                  {readers.length
                    ? `Read by ${readers.length}`
                    : messageStatus(message, snapshot)}
                </summary>
                <div className="absolute top-11 right-0 z-7 min-w-[130px] rounded-sm border border-border bg-popover p-2 text-popover-foreground">
                  {readers.length
                    ? readers.map((reader) => (
                        <span key={reader.memberId} className="block">
                          {reader.name}
                        </span>
                      ))
                    : "No read receipts yet"}
                </div>
              </details>
            ) : (
              own && <span>{status}</span>
            )}
          </div>
        )}
    </article>
  );
}
